import { RootState } from "@/lib/store";
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { fromUnixTime, isAfter } from "date-fns";
import { decode } from "jsonwebtoken";
import jwt_decode, { JwtPayload } from "jwt-decode";

import { setAccessToken } from "../reducers/user";
import { AppPayload } from "../types";
import {
  encodeToHash,
  transformGetRequest,
  transformPostRequest,
} from "./endpoints/functions";

const baseQuery = fetchBaseQuery({
  baseUrl: "",
  prepareHeaders: async (headers, { getState }) => {
    const accessToken = (getState() as RootState).user.accessToken;
    if (accessToken) {
      const payload: AppPayload = decode(accessToken) as AppPayload;
      headers.set("x-hub2-token", payload.token!);
    }
    headers.set("Content-type", "application/json");
    return headers;
  },
});

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const accessToken = (api.getState() as RootState).user.accessToken;
  let decoded: JwtPayload | string = "";
  try {
    decoded = jwt_decode(accessToken);
  } catch (e) {}
  const exp = fromUnixTime(decoded.exp!);
  const now = new Date();

  if (isAfter(exp, now) || !accessToken || !decoded || !decoded.exp) {
    ///
    if (typeof args !== "string") {
      const originalMethod = args?.method?.toLocaleLowerCase();

      const isConcurrent = args?.url?.includes("concurrent");

      let skipTransform = args.headers?.transform === "no";
      let sendDirectlyToBackend = false;

      // File uploads (body has a base64 data URL in `content`) bypass the
      // /api/app/post proxy to avoid Vercel's 4.5 MB serverless body limit.
      const body = args.body as { content?: unknown } | undefined;
      const isUpload =
        typeof body === "object" &&
        body !== null &&
        typeof body.content === "string" &&
        body.content.startsWith("data:");

      if (isUpload && args.url) {
        const path = args.url.replace(/^\//, "");
        args.url = `${process.env.NEXT_PUBLIC_SERVER_URL}/rest/dbo/${path}`;
        sendDirectlyToBackend = true;
      }

      if (!isConcurrent && !skipTransform && !sendDirectlyToBackend) {
        if (originalMethod === "get") transformGetRequest(args);
        if (originalMethod === "post") transformPostRequest(args);
        if (originalMethod === "delete") transformGetRequest(args, "delete");
        if (originalMethod === "put") transformPostRequest(args, "put");
      }

      if (skipTransform && args.headers) {
        delete (args.headers as Record<string, string>).transform;
      }

      if (isConcurrent) {
        args.body = { hash: encodeToHash(args.body) };
        args.url = `${window.location.origin}/api/app/concurrent`;
      }

      // if (sendDirectlyToBackend) {
      //   args.url = `API_URL/rest/dbo/${args.url}`;
      // }
    }

    return await baseQuery(args, api, extraOptions);
  } else {
    const tokenRefresh = await fetch(`/api/refresh_token`, {
      method: "post",
      credentials: "include",
    }).then((res) => res.json());

    if (tokenRefresh.accessToken) {
      api.dispatch(setAccessToken({ accessToken: tokenRefresh.accessToken }));
    }

    let result = await baseQuery(args, api, extraOptions);
    return result;
  }
};

export enum ObjectTypeAPI {
  Assets = "Assets",
  Visit = "Visit",
  VisitSheet = "VisitSheet",
  Buildings = "Buildings",
  Clients = "Clients",
  Contracts = "Contracts",
  Risks = "Risks",
  Users = "Users",
  Dashboards = "Dashboards",
  Generic = "Generic",
  Client = "Client",
  Subsystem = "Subsystem",
  ContractSubsystem = "ContractSubsystem",
  Building = "Building",
  Floorplan = "Floorplan",
  BoxDevice = "BoxDevice",
  DeviceFieldValue = "DeviceFieldValue",
  HelpBlock = "HelpBlock",
}
